<template>
  <div style="width: 100%">
    <div v-if="hasInitialLoad" class="as-master-page-container">
      <AsAMasterPage
        :dataStream="dataSource"
        :totalRecords="totalRecords"
        :currentPage="currentPage"
        :pageSize="pageSize"
        @search="handleSearch"
        @filter="handleFilter"
        @pageChange="handlePageChange"
        @pageSizeChange="handlePageSizeChange"
      />
    </div>
  </div>
</template>

<script>
import AsAMasterPage from "@components/AsAMasterPage/AsAMasterPage.vue";
import { getEmployeeMasterResponsibilites } from "@/api";

export default {
  components: {
    AsAMasterPage,
  },
  data() {
    return {
      dataSource: [],
      allData: [], // Store all data from API before filtering
      accountID: null,
      authUser: null,
      // Pagination state
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      searchQuery: "",
      searchDebounce: null,
      isSearching: false,
      hasInitialLoad: false,
      filters: {},
    };
  },
  async mounted() {
    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    this.authUser = JSON.parse(localStorage.getItem("user"));
    this.getMasterResponsibilities();
  },
  methods: {
    async getMasterResponsibilities() {
      try {
        this.$loader.show();
        // Fetch all data for frontend filtering
        let allResults = [];
        let offset = 0;
        const limit = 1000;
        let totalCount = null;

        do {
          const { data } = await getEmployeeMasterResponsibilites(
            this.accountID,
            limit,
            offset,
            this.searchQuery
          );

          if (data.results && data.results.length > 0) {
            allResults = allResults.concat(data.results);
            totalCount = data.count || 0;
            offset += limit;
          } else {
            break;
          }
          // Continue fetching until we have all records
        } while (
          allResults.length < totalCount &&
          allResults.length % limit === 0
        );

        this.hasInitialLoad = true;

        // Transform all data
        const transformedData =
          allResults.length > 0 ? this.transformData(allResults) : [];

        // Store all data
        this.allData = transformedData;

        // Apply frontend filters
        this.applyFilters();
      } catch (error) {
        console.error("Failed to fetch mastered responsibilities:", error);
        this.allData = [];
        this.dataSource = [];
        this.totalRecords = 0;
      } finally {
        this.$loader.hide();
      }
    },
    applyFilters() {
      // Check if we have data to filter
      if (!this.allData || this.allData.length === 0) {
        console.warn("applyFilters: allData is empty, cannot apply filters");
        this.dataSource = [];
        this.totalRecords = 0;
        return;
      }

      let filteredData = [...this.allData];

      // Apply checklist filters
      if (this.filters.no_checklist === true) {
        filteredData = filteredData.filter(
          (item) => (item.active_checkpoints_count || 0) === 0
        );
      } else if (this.filters.has_checklist === true) {
        filteredData = filteredData.filter(
          (item) => (item.active_checkpoints_count || 0) > 0
        );
      }

      // Apply coaches filters
      // Use originalCoaches array which stores the original coaches from API
      if (this.filters.no_coaches === true) {
        filteredData = filteredData.filter(
          (item) => !item.originalCoaches || item.originalCoaches.length === 0
        );
      } else if (this.filters.has_coaches === true) {
        filteredData = filteredData.filter(
          (item) => item.originalCoaches && item.originalCoaches.length > 0
        );
      }

      // Update total records count
      this.totalRecords = filteredData.length;

      // Apply pagination
      const offset = (this.currentPage - 1) * this.pageSize;
      this.dataSource = filteredData.slice(offset, offset + this.pageSize);
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.applyFilters();
    },
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.applyFilters();
    },
    handleSearch(query) {
      this.isSearching = query && query.trim() !== "";

      // Clear existing debounce timer
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }

      // Set new debounce timer
      this.searchDebounce = setTimeout(() => {
        this.searchQuery = query;
        this.currentPage = 1;
        // Re-fetch data when search changes (search is handled by backend)
        this.getMasterResponsibilities();
      }, 500); // 500ms debounce delay
    },
    handleFilter(filters) {
      // Ensure filters is an object, and clear it if empty
      this.filters = filters && Object.keys(filters).length > 0 ? filters : {};
      this.currentPage = 1;
      // Apply filters to existing data (no need to re-fetch)
      this.applyFilters();
    },
    transformData(dataArray) {
      return dataArray.map((data) => {
        // Transform coaches array to avatars format
        const transformCoachesToAvatars = (coaches) => {
          if (!coaches || !Array.isArray(coaches)) return [];
          return coaches.map((coach) => {
            const nameParts = (coach.name || "").trim().split(/\s+/);
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";
            return {
              img: coach.avatar || null,
              firstName: firstName,
              lastName: lastName,
            };
          });
        };

        return {
          ...data,
          id: data.id,
          name: data.name,
          code: this.extractCode(data.code_str),
          isCoach: data.coaches && data.coaches.length > 0,
          // Active checkpoint counts (preferred) - use active_* fields from API
          active_apprentice_checkpoints:
            data.active_apprentice_checkpoints || 0,
          active_professional_checkpoints:
            data.active_professional_checkpoints || 0,
          active_coach_checkpoints: data.active_coach_checkpoints || 0,
          active_master_checkpoints: data.active_master_checkpoints || 0,
          // Legacy checkpoint counts (fallback for backward compatibility)
          count_apprentice_checkpoints:
            data.active_apprentice_checkpoints ||
            data.count_apprentice_checkpoints ||
            0,
          count_professional_checkpoints:
            data.active_professional_checkpoints ||
            data.count_professional_checkpoints ||
            0,
          count_coach_checkpoints:
            data.active_coach_checkpoints || data.count_coach_checkpoints || 0,
          count_master_checkpoints:
            data.active_master_checkpoints ||
            data.count_master_checkpoints ||
            0,
          active_checkpoints_count: data.active_checkpoints_count || 0,
          draft_checkpoints_count: data.draft_checkpoints_count || 0,
          draft_apprentice_checkpoints: data.draft_apprentice_checkpoints || 0,
          draft_professional_checkpoints:
            data.draft_professional_checkpoints || 0,
          draft_coach_checkpoints: data.draft_coach_checkpoints || 0,
          draft_master_checkpoints: data.draft_master_checkpoints || 0,
          // Checkpoint object for LowLevelAssignments component
          checkpoint: {
            novice: data.novice_assignment_count || 0,
            apprentice: data.apprentice_assignment_count || 0,
            professional: data.professional_assignment_count || 0,
          },
          // Store original coaches array for filtering (before transformation)
          originalCoaches: data.coaches || [],
          // Coaches transformed to avatars format for MasteredResponsibilityTable
          coaches: {
            avatars: transformCoachesToAvatars(data.coaches),
            maxVisible: 3,
          },
          // Keep original data for reference
          masters: data.masters || [],
        };
      });
    },
    extractCode(str) {
      let match = str?.match(/#\d+/);
      return match ? match[0] : null;
    },
  },
  beforeDestroy() {
    // Clean up debounce timer
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  },
};
</script>

<style lang="css" scoped>
.as-master-page-container {
  padding: 20px;
}
</style>
